"use client";

import * as React from "react";

import { cn } from "../lib/utils";
import {
  ComboboxItems,
  ComboboxProps,
  ComboboxTrigger,
  ComboboxTriggerProps,
} from "./combobox";
import { Command } from "./command";
import { Pill } from "./pill";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- lint-backlog: empty type kept as an extension point; changing it can alter assignability
interface ComboboxPillsProps extends ComboboxProps {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- lint-backlog: empty type kept as an extension point; changing it can alter assignability
interface ComboboxPillsTriggerProps extends ComboboxTriggerProps {}

const ComboboxSelectedPills = ({
  className,
  store,
}: {
  className?: string;
  store: ComboboxProps["store"];
}): React.ReactNode => {
  const selectedPills = store.flatOptions
    .filter((v) => store.selected.includes(v.value))
    .map((v) => (
      <Pill
        // Avoid nested buttons, replace type of
        // root component with the child component.
        asChild
        key={v.value}
        onClick={(e) => {
          e.preventDefault();
          store.handleSelect(v.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            store.handleSelect(v.value);
          }
        }}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
        role="button"
        tabIndex={0}
      >
        <div>{v.label ?? v.value}</div>
      </Pill>
    ));

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {selectedPills}
    </div>
  );
};

// eslint-disable-next-line react/display-name -- lint-backlog: anonymous forwardRef/memo component; add a displayName in a follow-up
const ComboboxPillsTrigger = React.forwardRef<
  HTMLButtonElement,
  ComboboxPillsTriggerProps
>(({ className, store, ...props }, ref) => (
  <ComboboxTrigger
    className={cn(
      "h-fit [&>span]:pointer-events-auto",
      store.selected.length && "hover:bg-transparent",
      className,
    )}
    ref={ref}
    store={store}
    {...props}
  >
    {store.selected.length && <ComboboxSelectedPills store={store} />}
  </ComboboxTrigger>
));

/**
 * API reference: https://github.com/pacocoursey/cmdk
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/combobox
 *
 * @param options It expects a `options[i].value` to be unique to function
 * correctly. You can use `options[i].value` as both the `value` and `label`.
 *
 * If using `group`(s), every element in `options` **MUST** have a `group` property.
 */
const ComboboxPills = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- lint-backlog: binding kept on purpose (prop API, rest-spread exclusion or declaration-merge signature); removing it changes the API
  children,
  classes = {},
  searchValue,
  setSearchValue,
  store,
  ...props
}: ComboboxPillsProps) => {
  return (
    <Popover>
      <PopoverTrigger>
        {props.trigger ? (
          props.trigger({ className: classes.dropdownTrigger, store, ...props })
        ) : (
          <ComboboxPillsTrigger
            className={classes.dropdownTrigger}
            store={store}
            {...props}
          />
        )}
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command>
          <ComboboxItems
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            store={store}
          />
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export {
  ComboboxPills,
  ComboboxPillsTrigger,
  ComboboxSelectedPills,
  type ComboboxPillsProps,
  type ComboboxPillsTriggerProps,
};
